import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Setting Up React with Vite
//  Chapter 1 · Intro
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `# Check your versions
node -v    # Should be 18.0.0 or higher
npm -v     # Should be 9.0.0 or higher`;

const CODE_2 = `# Create project (interactive prompts)
npm create vite@latest my-react-app -- --template react

# Or with TypeScript template (recommended for larger projects)
npm create vite@latest my-react-app -- --template react-ts

cd my-react-app
npm install
npm run dev
# App is live at http://localhost:5173`;

const CODE_3 = `my-react-app/
  ├── public/               # Static assets (favicon, robots.txt)
  │    └── vite.svg
  ├── src/
  │    ├── assets/          # Images, fonts referenced in code
  │    ├── components/      # (you create this) reusable components
  │    ├── App.jsx           # Root component
  │    ├── App.css           # App-level styles
  │    ├── main.jsx          # Entry point — mounts React
  │    └── index.css         # Global styles
  ├── index.html             # Single HTML file (Vite entry)
  ├── vite.config.js         # Vite configuration
  └── package.json`;

const CODE_4 = `// vite.config.js
import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';
import path from 'path';

export default defineConfig({
  plugins: [react()],
  resolve: {
    alias: {
      '@': path.resolve(__dirname, './src'),  // import from '@/components/...'
    },
  },
  server: {
    port: 3000,
    proxy: {
      '/api': {
        target: 'http://localhost:8000',
        changeOrigin: true,
      },
    },
  },
});`;

const CODE_5 = `npm run dev      # Start dev server with HMR (Hot Module Replacement)
npm run build    # Production build → outputs to /dist
npm run preview  # Preview production build locally
npm run lint     # Run ESLint (if configured)`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function SettingUpReactWithVite() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 1 · Intro</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Setting Up React with Vite</h1>
        <p style={styles.desc}>Bootstrap a lightning-fast React development environment using Vite.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Why Vite? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Why Vite?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Vite (French for &#39;fast&#39;) is a next-generation build tool created by Evan You (Vue creator). It uses native ES Modules in development — no bundling needed — making cold starts and HMR near-instant. For production it uses Rollup to produce optimised bundles. It is now the <b>recommended way</b> to start a new React project." }} />
      </div>

      /* ── Prerequisites ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Prerequisites</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Before starting make sure you have:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Node.js 18+</b> installed (check: node -v)" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>npm 9+</b> or yarn/pnpm" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• A code editor — <b>VS Code</b> recommended" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• VS Code extensions: ESLint, Prettier, ES7+ React Snippets" }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Create a Vite React Project ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Create a Vite React Project</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Run the following commands in your terminal. Vite will scaffold the project, install dependencies, and give you a running dev server in seconds." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Project Structure Explained ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Project Structure Explained</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Vite generates a clean, minimal structure. Understanding each file from day one prevents confusion later." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Essential Vite Config ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Essential Vite Config</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Customise Vite for path aliases, proxying API calls in dev, and environment variables." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_4, 3)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 3 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_4)}</pre>
        </div>
      </div>

      /* ── Available Scripts ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Available Scripts</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Vite provides four essential scripts out of the box." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_5, 4)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 4 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_5)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What makes Vite faster than Create React App during development?</div>
          <div style={styles.qItem}>✦ 2. What port does Vite use by default?</div>
          <div style={styles.qItem}>✦ 3. What is HMR and why is it useful?</div>
          <div style={styles.qItem}>✦ 4. How do you configure a path alias in vite.config.js?</div>
          <div style={styles.qItem}>✦ 5. What command creates a TypeScript Vite React project?</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
